import { useState, useEffect, useCallback, useRef } from "react";
import {
	saveComponentSelections,
	saveDefaultProductPrices,
	updateComponentSelection,
	toggleMultipleComponentSelection,
	clearMultipleComponentSelections,
	clearComponentSelections,
	filterSelectionsToValidGroups,
	filterDefaultPricesToValidGroups,
	normalizeMultipleSelectionEntries,
	getSelectionsForGroup,
	type ComponentSelections,
	type ComponentSelection,
	type DefaultProductPrices,
} from "../utils/componentSelections";
import { SELECTION_TYPES } from "../types/viewmodel";
import {
	calculateBundlePrice,
	calculateBundlePriceForDefault,
	reconcilePriceFromSelections,
	type BundlePrice,
} from "../utils/priceCalculator";
import { STORE_DEFAULT_PRICE } from "../constants/productSelection";
import { useInitializationSource } from "./useInitializationSource";
import { useDefaultPrices } from "./useDefaultPrices";

interface ProductGroupData {
	id: string;
	selectionType?: string;
	product?: {
		id: string;
		name?: string;
		imageUrl?: string;
		stockStatus?: {
			iconClass?: string;
			description?: string;
		};
	};
	/** Default pre-selected products for Multiple selection type groups. */
	products?: {
		id: string;
		name?: string;
		imageUrl?: string;
		stockStatus?: {
			iconClass?: string;
			description?: string;
		};
	}[];
}

interface ProductData {
	description?: string;
	title?: string;
	description2?: string;
	imageUrl?: string;
	availabilityQuantity?: number;
	availabilityStatus?: string;
	calculatedStockQuantity?: number;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	priceDifference?: {
		net: number;
		gross: number;
	};
	price?: {
		net: number;
		gross: number;
	};
	defaultPrice?: {
		net: number;
		gross: number;
	};
	defaultProductId?: string;
}

interface UseProductSelectionOptions {
	configBundleId: string;
	initialPrice: BundlePrice;
	currentPrice: BundlePrice;
	productGroups: ProductGroupData[] | undefined;
	updatePrice: (price: BundlePrice) => void;
	initialSelections?: ComponentSelections | null;
	isUrlLoading?: boolean;
	allowStoredSelectionsFallback?: boolean;
}

interface UseProductSelectionReturn {
	selectedComponents: ComponentSelections;
	defaultProductPrices: DefaultProductPrices;
	staleSelectionsCount: number;
	isInitialized: boolean;
	handleProductSelect: (
		productGroupId: string,
		productId: string | null,
		productData?: ProductData,
	) => void;
	handleReset: () => void;
}

export function useProductSelection({
	configBundleId,
	initialPrice,
	currentPrice,
	productGroups,
	updatePrice,
	initialSelections = null,
	isUrlLoading = false,
	allowStoredSelectionsFallback = true,
}: UseProductSelectionOptions): UseProductSelectionReturn {
	const [selectedComponents, setSelectedComponents] =
		useState<ComponentSelections>({});
	const [staleSelectionsCount, setStaleSelectionsCount] = useState(0);

	const {
		defaultProductPrices,
		storeDefaultPrice,
		loadDefaultPrices,
		clearDefaults,
	} = useDefaultPrices({ configBundleId });

	const {
		selectionsToApply,
		shouldApply,
		isInitialized,
		markApplied,
		reset: resetInitSource,
	} = useInitializationSource({
		configBundleId,
		initialSelections,
		isUrlLoading,
		allowStoredSelectionsFallback,
	});

	const selectedComponentsRef = useRef(selectedComponents);
	const defaultProductPricesRef = useRef(defaultProductPrices);
	const currentPriceRef = useRef(currentPrice);

	useEffect(() => {
		selectedComponentsRef.current = selectedComponents;
	}, [selectedComponents]);

	useEffect(() => {
		defaultProductPricesRef.current = defaultProductPrices;
	}, [defaultProductPrices]);

	useEffect(() => {
		currentPriceRef.current = currentPrice;
	}, [currentPrice]);

	useEffect(() => {
		if (!shouldApply) return;
		if (!productGroups?.length) return;

		const validGroupIds = new Set(productGroups.map((g) => g.id));

		const storedDefaultPrices = loadDefaultPrices();
		const rawSelections = selectionsToApply ?? {};

		const selections = filterSelectionsToValidGroups(rawSelections, validGroupIds);

		const allGroupIds = new Set(productGroups.map((g) => g.id));
		const multipleGroupIds = new Set(
			productGroups
				.filter((g) => g.selectionType === SELECTION_TYPES.MULTIPLE)
				.map((g) => g.id),
		);
		const normalizedSelections = normalizeMultipleSelectionEntries(selections, multipleGroupIds, allGroupIds);
		const filteredDefaultPrices = filterDefaultPricesToValidGroups(storedDefaultPrices, validGroupIds);

		const strippedCount = Object.keys(rawSelections).length - Object.keys(normalizedSelections).length;
		if (strippedCount > 0) {
			saveComponentSelections(configBundleId, normalizedSelections);
			setStaleSelectionsCount(strippedCount);
		}
		if (Object.keys(storedDefaultPrices).length !== Object.keys(filteredDefaultPrices).length) {
			saveDefaultProductPrices(configBundleId, filteredDefaultPrices);
		}

		if (Object.keys(normalizedSelections).length > 0) {
			const reconciledPrice = reconcilePriceFromSelections(
				initialPrice,
				normalizedSelections,
				filteredDefaultPrices
			);

			if (reconciledPrice.net !== initialPrice.net || reconciledPrice.gross !== initialPrice.gross) {
				updatePrice(reconciledPrice);
			}

			if (selectionsToApply) {
				saveComponentSelections(configBundleId, normalizedSelections);
			}
		}

		setSelectedComponents(normalizedSelections);
		markApplied();
	}, [shouldApply, selectionsToApply, productGroups, initialPrice, updatePrice, configBundleId, loadDefaultPrices, markApplied]);

	const calculatePriceForClearing = useCallback(
		(
			currentPrice: BundlePrice,
			previousPriceDifference: { net: number; gross: number } | undefined,
			defaultPrice: { net: number; gross: number } | undefined,
		) => {
			const currentNet = currentPrice?.net ?? 0;
			const currentGross = currentPrice?.gross ?? 0;
			const diffNet = previousPriceDifference?.net ?? 0;
			const diffGross = previousPriceDifference?.gross ?? 0;
			const defaultNet = defaultPrice?.net ?? 0;
			const defaultGross = defaultPrice?.gross ?? 0;

			return {
				net: currentNet - diffNet - defaultNet,
				gross: currentGross - diffGross - defaultGross,
			};
		},
		[],
	);

	const calculatePriceAfterClear = useCallback(
		(
			currentPrice: BundlePrice,
			isSelectingDefault: boolean,
			defaultPrice: { net: number; gross: number } | undefined,
			newPriceDifference: { net: number; gross: number },
		) => {
			const currentNet = currentPrice?.net ?? 0;
			const currentGross = currentPrice?.gross ?? 0;
			const defaultNet = defaultPrice?.net ?? 0;
			const defaultGross = defaultPrice?.gross ?? 0;

			if (isSelectingDefault) {
				return {
					net: currentNet + defaultNet,
					gross: currentGross + defaultGross,
				};
			}

			return {
				net: currentNet + defaultNet + newPriceDifference.net,
				gross: currentGross + defaultGross + newPriceDifference.gross,
			};
		},
		[],
	);

	const createComponentSelection = useCallback(
		(
			productId: string | null,
			productData: ProductData | undefined,
			defaultProductId: string | undefined,
		): ComponentSelection => {
			if (productId === null) {
				return { productId: null };
			}

			return {
				productId,
				productName: productData?.title,
				productDescription: productData?.description,
				productDescription2: productData?.description2,
				imageUrl: productData?.imageUrl,
				availabilityQuantity: productData?.availabilityQuantity,
				availabilityStatus: productData?.availabilityStatus,
				calculatedStockQuantity: productData?.calculatedStockQuantity,
				stockStatus: productData?.stockStatus,
				priceDifference:
					productId === defaultProductId
						? { net: 0, gross: 0 }
						: productData?.priceDifference,
				price: productData?.price,
			};
		},
		[],
	);

	const handleProductSelect = useCallback(
		(productGroupId: string, productId: string | null, productData?: ProductData) => {
			if (!configBundleId) return;

			if (productId === STORE_DEFAULT_PRICE && productData?.defaultPrice && productData.defaultProductId) {
				storeDefaultPrice(
					productGroupId,
					productData.defaultPrice,
					productData.defaultProductId,
				);
				return;
			}

			const currentSelections = selectedComponentsRef.current;
			const currentDefaults = defaultProductPricesRef.current;
			const price = currentPriceRef.current;

			const productGroup = productGroups?.find((g) => g.id === productGroupId);
			const isMultipleSelection = productGroup?.selectionType === SELECTION_TYPES.MULTIPLE;

			// --- Multiple selection group logic ---
			if (isMultipleSelection && productId === null) {
				// Clear all selections for this group and reverse all price contributions.
				const existingEntries = getSelectionsForGroup(currentSelections, productGroupId);
				const totalPriceDiff = existingEntries.reduce(
					(acc, e) => ({
						net: acc.net + (e.priceDifference?.net ?? 0),
						gross: acc.gross + (e.priceDifference?.gross ?? 0),
					}),
					{ net: 0, gross: 0 },
				);
				updatePrice({
					net: (price.net ?? 0) - totalPriceDiff.net,
					gross: (price.gross ?? 0) - totalPriceDiff.gross,
				});
				const updatedSelections = clearMultipleComponentSelections(configBundleId, productGroupId);
				setSelectedComponents(updatedSelections);
				return;
			}

			if (isMultipleSelection && productId !== null) {
				// On first interaction with this group, seed sessionStorage with the group's
				// default products so they are preserved when the new product is toggled in.
				let seededEntries: ComponentSelection[] | undefined;
				if (productGroup && !Array.isArray(currentSelections[productGroupId])) {
					const defaultProducts =
						productGroup.products ??
						(productGroup.product ? [productGroup.product] : []);
					seededEntries = defaultProducts.map((p) => ({
						productId: p.id,
						productName: p.name,
						imageUrl: p.imageUrl,
						stockStatus: p.stockStatus,
						priceDifference: { net: 0, gross: 0 },
					}));
					saveComponentSelections(configBundleId, {
						...currentSelections,
						[productGroupId]: seededEntries,
					});
				}

				const existingEntries = seededEntries ?? getSelectionsForGroup(currentSelections, productGroupId);
				// When removing, prefer stored priceDifference so the caller doesn't need to pass productData.
				const existingEntry = existingEntries.find((s) => s.productId === productId);
				const isRemoving = existingEntry !== undefined;
				const priceDiff = isRemoving
					? (existingEntry?.priceDifference ?? productData?.priceDifference ?? { net: 0, gross: 0 })
					: (productData?.priceDifference ?? { net: 0, gross: 0 });
				const selection = createComponentSelection(productId, productData, productGroup?.product?.id);
				const newBundlePrice = isRemoving
					? {
							net: (price.net ?? 0) - (priceDiff.net ?? 0),
							gross: (price.gross ?? 0) - (priceDiff.gross ?? 0),
						}
					: {
							net: (price.net ?? 0) + (priceDiff.net ?? 0),
							gross: (price.gross ?? 0) + (priceDiff.gross ?? 0),
						};

				updatePrice(newBundlePrice);

				const updatedSelections = toggleMultipleComponentSelection(
					configBundleId,
					productGroupId,
					selection,
				);
				setSelectedComponents(updatedSelections);
				return;
			}

			// --- Single selection group logic (existing behaviour) ---
			const previousSelection = Array.isArray(currentSelections[productGroupId])
				? undefined
				: currentSelections[productGroupId];
			const previousPriceDifference = previousSelection?.priceDifference;
			const wasCleared = previousSelection?.productId === null;
			const defaultPriceInfo = currentDefaults[productGroupId];
			const defaultProductId = productGroup?.product?.id;

			let newBundlePrice: BundlePrice = price;

			if (productId === null) {
				newBundlePrice = calculatePriceForClearing(
					price,
					previousPriceDifference,
					defaultPriceInfo?.price,
				);
			} else if (wasCleared) {
				const isSelectingDefault = productId === defaultPriceInfo?.productId;
				const newPriceDifference = productData?.priceDifference || {
					net: 0,
					gross: 0,
				};

				newBundlePrice = calculatePriceAfterClear(
					price,
					isSelectingDefault,
					defaultPriceInfo?.price,
					newPriceDifference,
				);
			} else if (productId === defaultProductId && previousPriceDifference) {
				newBundlePrice = calculateBundlePriceForDefault({
					currentBundlePrice: price,
					previousProductPriceDifference: previousPriceDifference,
				});
			} else {
				const newPriceDifference = productData?.priceDifference || {
					net: 0,
					gross: 0,
				};
				newBundlePrice = calculateBundlePrice({
					currentBundlePrice: price,
					previousProductPriceDifference: previousPriceDifference || null,
					newProductPriceDifference: newPriceDifference,
				});
			}

			updatePrice(newBundlePrice);

			const selection = createComponentSelection(
				productId,
				productData,
				defaultProductId,
			);

			const updatedSelections = updateComponentSelection(
				configBundleId,
				productGroupId,
				selection,
			);
			setSelectedComponents(updatedSelections);
		},
		[
			configBundleId,
			productGroups,
			storeDefaultPrice,
			calculatePriceForClearing,
			calculatePriceAfterClear,
			createComponentSelection,
			updatePrice,
		],
	);

	const handleReset = useCallback(() => {
		if (!configBundleId) return;

		clearComponentSelections(configBundleId);
		clearDefaults();

		setSelectedComponents({});
		setStaleSelectionsCount(0);
		resetInitSource();

		updatePrice(initialPrice);
	}, [configBundleId, initialPrice, updatePrice, clearDefaults, resetInitSource]);

	return {
		selectedComponents,
		defaultProductPrices,
		staleSelectionsCount,
		isInitialized,
		handleProductSelect,
		handleReset,
	};
}